import { useState } from "react";
import { toast } from "react-toastify";
import "@/style/sass/_contentVersionHistorySelect.scss";
import ContentVersionNoteModal, {
  type ContentVersionNoteUpdate,
} from "@modals/ContentVersionNoteModal";
import type {
  ContentLanguageVersion,
  ContentVersionHistoryScope,
} from "@/utils/contentVersionHistory";
import { saveContentVersionNoteAsync } from "@/utils/contentVersionHistoryApi";

type ContentVersionHistoryButtonProps = {
  versions: ContentLanguageVersion[];
  disabled?: boolean;
  className?: string;
  historyType: ContentVersionHistoryScope;
  onVersionNoteUpdated: (version: ContentLanguageVersion) => void;
};

export default function ContentVersionHistoryButton({
  versions,
  disabled = false,
  className = "",
  historyType,
  onVersionNoteUpdated,
}: ContentVersionHistoryButtonProps) {
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  if (!Array.isArray(versions) || versions.length === 0) {
    return null;
  }

  async function handleSaveVersionNotesAsync(updates: ContentVersionNoteUpdate[]) {
    if (updates.length === 0) {
      setIsNoteModalOpen(false);
      return;
    }

    setIsSavingNote(true);
    try {
      for (const update of updates) {
        const updated = await saveContentVersionNoteAsync({
          historyType,
          historyId: update.id,
          changeNote: update.changeNote,
        });
        if (!updated) {
          toast.error("Failed to save version notes.");
          return;
        }

        onVersionNoteUpdated(updated);
      }

      toast.success(updates.length === 1 ? "Version note saved." : "Version notes saved.");
      setIsNoteModalOpen(false);
    } catch (error) {
      console.error("Failed to save content version notes:", error);
      toast.error("Failed to save version notes.");
    } finally {
      setIsSavingNote(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`btn btn-secondary contentVersionHistoryButton ${className}`.trim()}
        disabled={disabled}
        title="View and edit version notes"
        aria-label="View and edit version notes"
        onClick={() => setIsNoteModalOpen(true)}
      >
        Notes
      </button>

      <ContentVersionNoteModal
        isOpen={isNoteModalOpen}
        versions={versions}
        isSaving={isSavingNote}
        onClose={() => setIsNoteModalOpen(false)}
        onSave={handleSaveVersionNotesAsync}
      />
    </>
  );
}
